/**
 * Parental Controls Dashboard
 *
 * Shown at `/account/parental-controls`. The authenticated user sees every
 * child account that lists them as the parent (via parentEmail match on the
 * server). Per COPPA §312.6 the parent can review, revoke consent, or
 * request account deletion. Voice-retention opt-in toggle ships in W3.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import {
  listMyChildren,
  listConsentRecords,
  withdrawConsent,
  requestConsentResend,
  ChildSummary,
  ParentalConsentRecord,
} from '../../services/parentalConsentService';
import { ApiError } from '../../types/auth';

const statusBadgeClass = (status: ChildSummary['parentConsentStatus']): string => {
  switch (status) {
    case 'GRANTED':
      return 'bg-green-100 text-green-800';
    case 'PENDING':
      return 'bg-amber-100 text-amber-800';
    case 'REVOKED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const actionBadge = (action: ParentalConsentRecord['action']): string => {
  switch (action) {
    case 'GRANTED':
      return 'text-green-700';
    case 'REVOKED':
    case 'EXPIRED':
      return 'text-red-700';
    case 'REQUEST_SENT':
    case 'RESENT':
      return 'text-amber-700';
    default:
      return 'text-gray-700';
  }
};

const ParentalControlsPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [records, setRecords] = useState<ParentalConsentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const refreshChildren = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listMyChildren();
      setChildren(list);
      if (list.length > 0 && !selectedChildId) setSelectedChildId(list[0].id);
    } catch (err) {
      setError((err as ApiError).message || t('parentalControls.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedChildId, t]);

  useEffect(() => {
    refreshChildren();
  }, [refreshChildren]);

  useEffect(() => {
    if (!selectedChildId) {
      setRecords([]);
      return;
    }
    listConsentRecords(selectedChildId)
      .then(setRecords)
      .catch((err: ApiError) => setError(err.message));
  }, [selectedChildId]);

  const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(i18n.language);
  };

  const handleRevoke = async (child: ChildSummary) => {
    if (!window.confirm(t('parentalControls.confirmRevoke', { child: child.fullName || child.username }))) return;
    setActionLoadingId(child.id);
    try {
      await withdrawConsent(child.id, child.email);
      await refreshChildren();
    } catch (err) {
      setError((err as ApiError).message || t('parentalControls.revokeError'));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResend = async (child: ChildSummary) => {
    setActionLoadingId(child.id);
    try {
      await requestConsentResend(child.id);
      await refreshChildren();
    } catch (err) {
      setError((err as ApiError).message || t('parentalControls.resendError'));
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-32 bg-gray-100 rounded" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">{t('parentalControls.title')}</h1>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600 mb-4">{t('parentalControls.noChildren')}</p>
          <Link to="/" className="text-primary-600 hover:underline">
            {t('parentalControls.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  const selected = children.find((c) => c.id === selectedChildId) ?? null;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">{t('parentalControls.title')}</h1>
      <p className="text-gray-600 mb-6">{t('parentalControls.subtitle')}</p>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {/* Children list */}
        <div className="md:col-span-1 space-y-2">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {t('parentalControls.childrenHeading')}
          </h2>
          {children.map((child) => {
            const isActive = child.id === selectedChildId;
            return (
              <button
                key={child.id}
                onClick={() => setSelectedChildId(child.id)}
                className={`w-full text-left p-3 rounded-lg border transition ${
                  isActive
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <p className="font-medium text-gray-900">{child.fullName || child.username}</p>
                <p className="text-xs text-gray-500 mt-1">{child.email}</p>
                <span
                  className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(
                    child.parentConsentStatus,
                  )}`}
                >
                  {t(`parentalControls.status.${child.parentConsentStatus}`)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Selected child detail */}
        <div className="md:col-span-2">
          {selected && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selected.fullName || selected.username}
                  </h3>
                  <p className="text-sm text-gray-500">{selected.email}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded text-sm font-medium ${statusBadgeClass(
                    selected.parentConsentStatus,
                  )}`}
                >
                  {t(`parentalControls.status.${selected.parentConsentStatus}`)}
                </span>
              </div>

              <dl className="grid grid-cols-2 gap-4 text-sm mb-6">
                <div>
                  <dt className="text-gray-500">{t('parentalControls.grantedAt')}</dt>
                  <dd className="text-gray-900 mt-1">{formatDate(selected.parentConsentAt)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('parentalControls.expiresAt')}</dt>
                  <dd className="text-gray-900 mt-1">{formatDate(selected.parentConsentExpiresAt)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('parentalControls.accountStatus')}</dt>
                  <dd className="text-gray-900 mt-1">{selected.status}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('parentalControls.voiceRetention')}</dt>
                  <dd className="text-gray-900 mt-1">
                    {selected.childVoiceRetentionConsent
                      ? t('parentalControls.voiceOptedIn')
                      : t('parentalControls.voiceOptedOut')}
                  </dd>
                </div>
              </dl>

              <div className="flex flex-wrap gap-3 mb-6">
                {selected.parentConsentStatus === 'GRANTED' && (
                  <Button
                    variant="outline"
                    onClick={() => handleRevoke(selected)}
                    isLoading={actionLoadingId === selected.id}
                    disabled={actionLoadingId === selected.id}
                  >
                    {t('parentalControls.revoke')}
                  </Button>
                )}
                {selected.parentConsentStatus === 'PENDING' && (
                  <Button
                    variant="outline"
                    onClick={() => handleResend(selected)}
                    isLoading={actionLoadingId === selected.id}
                    disabled={actionLoadingId === selected.id}
                  >
                    {t('parentalControls.resend')}
                  </Button>
                )}
              </div>

              {/* Audit log */}
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t('parentalControls.auditLog')}
              </h4>
              <div className="border-t border-gray-100">
                {records.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">{t('parentalControls.noRecords')}</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {records.map((r) => (
                      <li key={r.id} className="py-3 flex items-start justify-between gap-4">
                        <div>
                          <p className={`text-sm font-medium ${actionBadge(r.action)}`}>
                            {t(`parentalControls.action.${r.action}`)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatDate(r.createdAt)}</p>
                        </div>
                        {r.ipAddress && (
                          <p className="text-xs text-gray-400 font-mono">{r.ipAddress}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentalControlsPage;
